import {
  convertInstalmentObj,
  convertObj,
  convertPaylaterAdminFinancialTransactionObj,
} from './order/convert.js'
import * as orderUtils from './order/order-utils.js'
import * as insUtils from './order/instalment-utils.js'
import * as orderPaynowUtils from './order/paynow-order-consumer.js'
import * as orderDivitMilesUtils from './order/divit-miles.js'
import * as CONST from './constants/type.js'
import { initPaynowOrder } from './order/paynow-order.js'

export const Order = (orderObjParam, orderType, source) => {
  const data = orderObjParam
    ? convertObj(orderObjParam, orderType, source)
    : initPaynowOrder

  const allTransactions =
    orderType === 'paylater'
      ? data.instalments
          ?.flatMap(({ financialTx }) => financialTx)
          .filter(transaction => !!transaction)
      : data?.financialTransactions

  const currency = data.orderAmount.currency || ''

  const getOrderTotal = () => {
    return orderUtils.getOrderAmount(data)
  }

  const getOutstandingAmount = () => {
    return orderUtils.getOrderOutstandingAmount(
      data,
      allTransactions,
      orderType,
      source
    )
  }

  const getTotalCashPaid = () => {
    return orderUtils.getTotalCashPaid(data, allTransactions)
  }

  const getTotalMilesPaid = () => {
    return orderUtils.getTotalMilesPaid(data, allTransactions)
  }

  const getMerchantServiceFee = () => {
    return data.merchantServiceFee
  }

  const getCustomerServiceFee = () => {
    return data.customerServiceFee
  }

  const getDivitFee = () => {
    return orderUtils.getDivitFee(data)
  }

  const hasLatefee = () => {
    return false
  }

  const getRefundRequested = () => {
    return orderUtils.getRefundRequestedAmount(data, allTransactions)
  }

  const getRefundCompleted = () => {
    return orderUtils.getRefundCompletedAmount(data, allTransactions)
  }

  const getCancelledAmount = () => {
    return orderUtils.getCancelledAmount(data, allTransactions)
  }

  const getTotalPaid = () => {
    return orderUtils.getTotalPaidAmount(data, allTransactions)
  }

  const getTotalRefund = () => {
    return orderUtils.getTotalRefundAmount(data, allTransactions)
  }

  const getServiceFee = () => {
    return orderUtils.getServiceFeeAmount(data, orderType)
  }

  const getOffsetAmount = () => {
    return orderUtils.getOffsetAmountObj(data, allTransactions)
  }

  const getBurnedAmount = () => {
    return orderUtils.getTotalMilesPaid(data, allTransactions)
  }

  const getTotalLateFee = () => {
    return orderUtils.getTotalLateFee(data, orderType)
  }

  const getMilesUnit = () => {
    return orderUtils.getMilesUnit(data, orderType)
  }

  const getMilesBurned = () => {
    return orderUtils.getMilesBurned(allTransactions, orderType)
  }

  const getMilesCostAmount = () => {
    return orderUtils.getMilesCostAmount(data, orderType)
  }

  const getMerchantReceivableAmount = () => {
    return orderUtils.getMerchantReceivableAmount(data, orderType)
  }

  const hasCancelled = orderUtils.hasRefundCompleted(data, orderType)
  const hasRefundRequested = orderUtils.hasRefundRequested(data, orderType)
  const hasRefundCompleted = orderUtils.hasRefundCompleted(data, orderType)

  return {
    data,
    currency,
    hasCancelled,
    hasRefundRequested,
    hasRefundCompleted,
    getOrderTotal,
    getOutstandingAmount,
    getTotalCashPaid,
    getTotalMilesPaid,
    getMerchantServiceFee,
    getCustomerServiceFee,
    hasLatefee,
    getRefundRequested,
    getRefundCompleted,
    getCancelledAmount,
    getTotalPaid,
    getTotalRefund,
    getServiceFee,
    getOffsetAmount,
    getBurnedAmount,
    getTotalLateFee,
    getMilesUnit,
    getMilesBurned,
    getMilesCostAmount,
    getDivitFee,
    getMerchantReceivableAmount,
  }
}

export const Instalments = (orderObjParam, orderType, source) => {
  const data = orderObjParam ? convertObj(orderObjParam, orderType, source) : []
  const getInstalmentByNo = instalmentNo => {
    const instalments = (data?.instalments || []).find(
      i => i.series == instalmentNo
    )
    return instalments && instalments.length == 1 ? instalments[0] : {}
  }

  const getInstalmentByID = instalmentID => {
    const instalments = (data?.instalments || []).find(
      i => i.instalmentID == instalmentID
    )
    return instalments && instalments.length == 1 ? instalments[0] : {}
  }

  const getOutstandingAmount = instalment => {
    instalment = convertInstalmentObj(instalment, orderType)
    return insUtils.getOutstandingAmount(instalment, data, orderType)
  }

  const hasBurned = instalment => {
    instalment = convertInstalmentObj(instalment, orderType)
    if (!instalment.financialTx) return false
    return instalment.financialTx.find(
      tx =>
        tx.type === CONST.TXTYPE.PAYMENT &&
        tx.status === CONST.TXSTATUS.COMPLETE
    )
  }

  const getBurnedMilesAmount = instalment => {
    return instalment.miles
  }

  const getCashPaymentAmount = instalment => {
    return instalment.cashAmount
  }

  const getTotalCredit = instalment => {
    const result = instalment.financialTx.reduce((sum, tx) => {
      const { minusCredit } = FinancialTransaction(tx, orderType, source)
      return minusCredit() + sum
    }, 0)
    return result
  }

  const getTotalDebit = instalment => {
    const result = instalment.financialTx.reduce((sum, tx) => {
      const { minusDebit } = FinancialTransaction(tx, orderType, source)
      return minusDebit() + sum
    }, 0)

    return result
  }

  const getBalanceNumber = instalment => {
    instalment = convertInstalmentObj(instalment, orderType)
    return insUtils.getBalance(instalment, orderType)
  }

  return {
    data,
    getInstalmentByNo,
    getInstalmentByID,
    getOutstandingAmount,
    getBurnedMilesAmount,
    getCashPaymentAmount,
    getTotalCredit,
    getTotalDebit,
    hasBurned,
    getBalanceNumber,
  }
}

export const FinancialTransaction = (txObjParam, orderType, source) => {
  let tx = []

  switch (orderType) {
    case 'paylater':
      tx =
        source === 'admin'
          ? convertPaylaterAdminFinancialTransactionObj(txObjParam)
          : convertPaylaterAdminFinancialTransactionObj(txObjParam)
      break
    case 'paynow':
      tx = txObjParam
      break
  }

  const minusDebit = () => {
    if (
      [
        CONST.TXTYPE.INSTALMENT,
        CONST.TXTYPE.REFUND,
        CONST.TXTYPE.ADJUSTMENT,
        CONST.TXTYPE.OFFSET,
        CONST.TXTYPE.NOSHOW,
      ].indexOf(tx.type) >= 0
    ) {
      return tx.amount.amount
    }

    if (
      tx.type === CONST.TXTYPE.LATEFEE &&
      tx.status.toLowerCase() === CONST.TXSTATUS.COMPLETE
    ) {
      return tx.amount.amount
    }

    if (tx.payment_instrument) {
      const type = tx.payment_instrument.displayName.toLowerCase()

      if (
        [
          CONST.TXTYPE.INSTALMENT,
          CONST.TXTYPE.ADJUSTMENT,
          CONST.TXTYPE.LATEFEE,
          CONST.TXTYPE.NOSHOW,
          CONST.TXTYPE.OFFSET,
        ].indexOf(type) >= 0
      ) {
        return -tx.amount.amount
      }
    }

    return ''
  }

  const minusCredit = () => {
    if (
      [
        CONST.TXTYPE.REFUND,
        CONST.TXTYPE.ADJUSTMENT,
        CONST.TXTYPE.OFFSET,
        CONST.TXTYPE.NOSHOW,
        CONST.TXTYPE.INSTALMENT,
      ].indexOf(tx.type) >= 0
    ) {
      return ''
    }

    if (tx.payment_instrument) {
      const type = tx.payment_instrument.displayName.toLowerCase()

      if (
        type === CONST.TXSTATUS.REALLOCATED ||
        type === CONST.TXSTATUS.ASSIGN
      ) {
        return -tx.amount.amount
      }
      if (
        ((type === CONST.PAYMENT_INSTRUMENT.FPS ||
          type === CONST.PAYMENT_INSTRUMENT.EDDA) &&
          tx.status.toLowerCase() === CONST.TXSTATUS.COMPLETE) ||
        type === CONST.PAYMENT_INSTRUMENT.MILE_SPENT ||
        type === CONST.PAYMENT_INSTRUMENT.ADJPAY
      ) {
        return tx.amount.amount
      }
    }

    return ''
  }

  return { minusDebit, minusCredit }
}

export const OrderPaynow = (orderObjParam, paidby) => {
  const moneyConvert =
    paidby === 'promptpay' ? 4.61 : paidby === 'paynow' ? 0.17 : 1

  const currencyConvert = currency =>
    paidby === 'promptpay' ? 'THB' : paidby === 'paynow' ? 'SGD' : currency

  const getPartnerRef = () => {
    return orderPaynowUtils.getPartnerRef(orderObjParam)
  }

  const getOrderAmount = () => {
    return {
      amount:
        orderPaynowUtils.getOrderAmount(orderObjParam).amount * moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getOrderAmount(orderObjParam).currency
      ),
    }
  }
  const getTotalPaidAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalPaidAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalPaidAmount(orderObjParam).currency
      ),
    }
  }

  const isRefundPending = () => {
    return orderPaynowUtils.isRefundPending(orderObjParam)
  }
  const isRefunded = () => {
    return orderPaynowUtils.isRefunded(orderObjParam)
  }
  const getTotalRefundedAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalRefundedAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalRefundedAmount(orderObjParam).currency
      ),
    }
  }
  const getTotalOffsetAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalOffsetAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalOffsetAmount(orderObjParam).currency
      ),
    }
  }
  const getMilesBurned = () => {
    return orderPaynowUtils.getMilesBurned(orderObjParam)
  }

  const isNormal = () => {
    return orderPaynowUtils.isNormal(orderObjParam)
  }

  const isEnd = () => {
    return orderPaynowUtils.isEnd(orderObjParam)
  }

  const getInstalmentAmount = () => {
    return {
      amount:
        orderPaynowUtils.getInstalmentAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getInstalmentAmount(orderObjParam).currency
      ),
    }
  }

  const getTotalOutstandingAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalOutstandingAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalOutstandingAmount(orderObjParam).currency
      ),
    }
  }

  const isPaid = orderPaynowUtils.isPaid(orderObjParam)

  const isCancelled = orderPaynowUtils.isCancelled(orderObjParam)

  const isUnderPay = orderPaynowUtils.isOverPay(orderObjParam)

  const isOverPay = orderPaynowUtils.isOverPay(orderObjParam)

  const getTotalRequestedRefundAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalRequestedRefundAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalRequestedRefundAmount(orderObjParam).currency
      ),
    }
  }

  const getTotalAdjustedAmount = () => {
    return {
      amount:
        orderPaynowUtils.getTotalAdjustedAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getTotalAdjustedAmount(orderObjParam).currency
      ),
    }
  }

  const getOverPaidAmount = () => {
    return {
      amount:
        orderPaynowUtils.getOverPaidAmount(orderObjParam).amount * moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getOverPaidAmount(orderObjParam).currency
      ),
    }
  }

  const getLatestTransaction = moment => {
    return orderPaynowUtils.getLatestTransaction(orderObjParam, moment)
  }

  const isPaymentInstrumentProcessing = instrument => {
    return orderPaynowUtils.isPaymentInstrumentProcessing(
      orderObjParam,
      instrument
    )
  }

  const isFailed = moment => {
    return orderPaynowUtils.isFailed(orderObjParam, moment)
  }

  const isRedirectFailureNotAvailable = () => {
    return orderPaynowUtils.isRedirectFailureNotAvailable(orderObjParam)
  }

  const isCompleted = () => {
    return orderPaynowUtils.isCompleted(orderObjParam)
  }

  const isExpired = () => {
    return orderPaynowUtils.isExpired(orderObjParam)
  }

  const getOutstandingAmount = () => {
    return {
      amount:
        orderPaynowUtils.getOutstandingAmount(
          orderObjParam.instalments[0],
          orderObjParam.financialTransactions,
          false
        ).amount * moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getOutstandingAmount(
          orderObjParam.instalments[0],
          orderObjParam.financialTransactions,
          false
        ).currency
      ),
    }
  }

  const getPaidByMilesAmount = () => {
    return {
      amount:
        orderPaynowUtils.getPaidByMilesAmount(orderObjParam).amount *
        moneyConvert,
      currency: currencyConvert(
        orderPaynowUtils.getPaidByMilesAmount(orderObjParam).currency
      ),
    }
  }

  const isRedirectSuccessNotAvailable = () => {
    return orderPaynowUtils.isRedirectSuccessNotAvailable(orderObjParam)
  }

  return {
    getPartnerRef,
    getOrderAmount,
    getTotalPaidAmount,
    isRefundPending,
    isRefunded,
    getTotalRefundedAmount,
    getTotalOffsetAmount,
    getMilesBurned,
    isNormal,
    isEnd,
    getInstalmentAmount,
    getTotalOutstandingAmount,
    isCancelled,
    isPaid,
    isUnderPay,
    isOverPay,
    getTotalRequestedRefundAmount,
    getTotalAdjustedAmount,
    getOverPaidAmount,
    getLatestTransaction,
    isPaymentInstrumentProcessing,
    isFailed,
    isRedirectFailureNotAvailable,
    isCompleted,
    isExpired,
    getOutstandingAmount,
    getPaidByMilesAmount,
    isRedirectSuccessNotAvailable,
    moneyConvert,
    currencyConvert,
  }
}

export const DivitMiles = (order, orderMiles) => {
  const getMilesEarned = () => {
    return orderDivitMilesUtils.getMilesEarned(orderMiles)
  }
  const getBonusEvents = () => {
    return orderDivitMilesUtils.getBonusEvents(orderMiles)
  }
  const getTotalMilesCancelled = () => {
    return orderDivitMilesUtils.getTotalMilesCancelled(orderMiles)
  }
  const getTotalMilesRefunded = () => {
    return orderDivitMilesUtils.getTotalMilesRefunded(order)
  }

  return {
    getMilesEarned,
    getBonusEvents,
    getTotalMilesCancelled,
    getTotalMilesRefunded,
  }
}
